import { Injectable } from '@angular/core';

/**
 * Stores the third-party (TP) session identifier for the lifetime of the
 * browser session so it can be attached as the `sessionId` request header on
 * calls to the Policy Orchestrator.
 *
 * For third-party requests the Orchestrator authenticates using the `sessionId`
 * carried in the request header (no `Authorization` header is sent). The
 * `sessionId` originates from the entry URL query param and is captured here so
 * that services and NgRx effects can read it without threading it through every
 * call site.
 */
@Injectable({
    providedIn: 'root',
})
export class SessionService {
    private readonly SESSION_KEY = 'tpSessionId';

    /**
     * Persists the third-party session identifier. No-op for empty values.
     */
    setSessionId(sessionId: string | null | undefined): void {
        if (!sessionId) return;
        sessionStorage.setItem(this.SESSION_KEY, sessionId);
    }

    /**
     * Returns the stored third-party session identifier, or null when absent.
     */
    getSessionId(): string | null {
        return sessionStorage.getItem(this.SESSION_KEY);
    }

    /**
     * True when a non-empty third-party session identifier is stored.
     */
    hasSessionId(): boolean {
        return !!this.getSessionId();
    }

    /**
     * Removes the stored third-party session identifier.
     */
    clearSessionId(): void {
        sessionStorage.removeItem(this.SESSION_KEY);
    }
}
